import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { DetailFooterSlot } from './detail-footer-slot';

export interface DetailCrumb {
  readonly label: string;
  /** Steps back to this place; the last crumb (the open item) never has one. */
  readonly action?: () => void;
}

/**
 * stitch-app-shell `Breadcrumbs`: the detail panel's 48px footer row, naming where the open item sits
 * (workspace › app › view › item). Earlier crumbs that have an action are links back; a path too long for
 * the row collapses to "… › … › item" (a "…" shows its crumb), and the item truncates if it still does not fit.
 * On a container page it also carries the page's pager (Refresh ‹ › on the
 * left, the count on the right), through {@link DetailFooterSlot}.
 */
@Component({
  selector: 'app-detail-breadcrumbs',
  templateUrl: './detail-breadcrumbs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, NgTemplateOutlet],
  // 49px with its line, as panels 2 and 4's pager rows, so the three panels' foot rows line up to the pixel; Off
  // White as theirs (Johanna, 2026-10-22).
  host: { class: 'flex h-[49px] shrink-0 items-center gap-6 border-t border-gray-200 bg-offwhite px-8' },
})
export class DetailBreadcrumbsComponent {
  public readonly crumbs = input.required<readonly DetailCrumb[]>();
  /** A quiet note after the path, such as "Archived". */
  public readonly status = input<string>();

  /** The container page's slot (its form claims it when it shows breadcrumbs): the pager drawn in this row. */
  protected readonly footerSlot = inject(DetailFooterSlot, { optional: true });

  /** The full path does not fit the row: the earlier crumbs read "…". */
  protected readonly collapsed = signal(false);
  /** The crumbs whose "…" was clicked, shown in full until the path changes. */
  protected readonly revealed = signal<ReadonlySet<number>>(new Set());

  private readonly nav = viewChild<ElementRef<HTMLElement>>('nav');
  private readonly measure = viewChild<ElementRef<HTMLElement>>('measure');
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // Keyed on the path's text, not the crumbs array: a form builds a new array on every render, and resetting
    // `revealed` for each one re-rendered the form for ever (the page hung).
    effect(() => {
      this.path();
      untracked(() => {
        if (this.revealed().size) this.revealed.set(new Set());
      });
      queueMicrotask(() => this.fit());
    });

    afterNextRender(() => {
      this.fit();
      this.zone.runOutsideAngular(() => {
        const observer = new ResizeObserver(() => this.fit());
        const nav = this.nav()?.nativeElement;
        if (nav) observer.observe(nav);
        this.destroyRef.onDestroy(() => observer.disconnect());
      });
    });
  }

  protected reveal(index: number): void {
    this.revealed.update((set) => new Set(set).add(index));
  }

  private fit(): void {
    const nav = this.nav()?.nativeElement;
    const measure = this.measure()?.nativeElement;
    if (!nav || !measure) return;
    const collapsed = measure.scrollWidth > nav.clientWidth + 1;
    if (collapsed !== this.collapsed()) {
      this.zone.run(() => this.collapsed.set(collapsed));
    }
  }

  protected readonly path = computed(() =>
    this.crumbs()
      .map(({ label }) => label)
      .join(' › '),
  );
}
